import store from '../../../store/index';
import { Route } from 'vue-router';
import { RoutingRouteEnum } from '../../routes.enum';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import {
  isPartnerAuthenticatedLocally,
  resetStorageAndGlobalStateAuth,
  resetStorageAndGlobalStatePermissions,
} from '@/api/useAuthStatus';
import { PartnerRoleEnum } from '@/types/models/partner.type';
import mixpanel from 'mixpanel-browser';

const { useGetters } = createNamespacedHelpers(store, 'authentication');

export const useAuthentication = async (to: Route, from: Route, next: any) => {
  const { isPartnerAuthenticated, role } = useGetters(['isPartnerAuthenticated', 'role', 'isAccountOwner']);

  // log out
  if (to.name === RoutingRouteEnum.Logout && to.params.logout === 'logout') {
    await resetStorageAndGlobalStateAuth();
    await resetStorageAndGlobalStatePermissions();
    mixpanel.reset();
    return next({ name: RoutingRouteEnum.LogIn });
  }

  //from any route to route that requires authentication
  if (to.matched.some((route) => route.meta.requireAuth)) {
    if (isPartnerAuthenticatedLocally() || isPartnerAuthenticated.value) next();
    else next({ name: RoutingRouteEnum.LogIn, query: { redirect: to.fullPath } });
  }

  //from any route to route that doesn not require authentication
  if (!to.matched.some((route) => route.meta.requireAuth)) {
    // Public routes that must still run for already-authenticated users
    // (e.g. email confirmation link, password reset link). `/login/otp` is
    // in the list too so a lingering `isPartnerAuthenticatedLocally()`
    // from a previous session can't bounce a partner mid-2FA flow — the
    // OTP screen itself handles the "no challenge" case by redirecting
    // back to `/login`.
    const publicRoutesAllowedWhenAuthenticated: Array<RoutingRouteEnum> = [
      RoutingRouteEnum.VerifyEmail,
      RoutingRouteEnum.ChangePassword,
      RoutingRouteEnum.ForgotPassword,
      RoutingRouteEnum.LogIn_Otp,
    ];
    if (
      typeof to.name === 'string'
      && publicRoutesAllowedWhenAuthenticated.includes(to.name as RoutingRouteEnum)
    ) {
      return next();
    }

    if (isPartnerAuthenticatedLocally()) {
      switch (role.value) {
        case null:
          next({ name: RoutingRouteEnum.Home });
          return;
        case PartnerRoleEnum.PartnershipAdmin:
          next({ name: RoutingRouteEnum.ManageAgreements_Apps });
          return;
        case PartnerRoleEnum.Admin:
          next({ name: RoutingRouteEnum.Admin_AppsManagement });
          return;
        case PartnerRoleEnum.Finance:
          next({ name: RoutingRouteEnum.FinanceAdmin_AppsSubscriptions });
          return;
        default:
          next({ name: RoutingRouteEnum.Home });
          return;
      }
    } else next();
  }
};
